// src/pages/Projeler/ProjeDetayPage.tsx  (mevcut dosya yolunuza uyarlayın)
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  X,
  ZoomIn,
  ZoomOut,
  Maximize,
  Minimize,
  Image as ImageIcon,
} from "lucide-react";
import { useTheme } from "../../../../context/ThemeContext";
import { postRequest } from "../../../../api/postRequest";

/* ==== Types ==== */
interface ApiResponse<T> {
  success: boolean;
  errorCode?: string;
  errorDetail?: string;
  data?: T;
}
type Fit = "cover" | "contain";
type GalleryImageJson = { url: string; alt?: string; siraNo?: number; fit?: Fit };
type StepImageJson = GalleryImageJson;
type StepJson = { adim: string; aciklama: string; tarih?: string | null; fotolar?: StepImageJson[] };

interface ProjeDetailDto {
  id: number;
  baslik: string;
  aciklama?: string | null;
  kapakFotoUrl?: string | null;
  siraNo: number;
  aktifMi: boolean;
  olusturmaTarihi: string;
  guncellemeTarihi?: string | null;
  fotoGaleriJson?: string | null;
  yapimAsamalariJson?: string | null;
  etiketlerJson?: string | null;
}

/* ==== helpers ==== */
const parseArr = <T,>(j?: string | null): T[] => {
  if (!j) return [];
  try {
    const v = JSON.parse(j);
    return Array.isArray(v) ? (v as T[]) : [];
  } catch {
    return [];
  }
};

/* ==== Theme helpers (surface & divider) ==== */
const hexToRgb = (hex: string) => {
  const h = (hex || "#0f1115").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const luminance = ({ r, g, b }: { r: number; g: number; b: number }) => {
  const f = (v: number) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};

type Surfaces = {
  surface1: string;
  surface2: string;
  surface3: string;
  divider: string;
  softShadow: string;
  textPrimary: string;
  textSecondary: string;
  accent: string;
  bg: string;
  isDark: boolean;
};

const useSurfaces = (theme?: any): Surfaces => {
  const accent = theme?.accent ?? "#22c55e";
  const textPrimary = theme?.textPrimary ?? "#111827";
  const textSecondary = theme?.textSecondary ?? "#6b7280";
  const bg = theme?.backgroundColor || "#0f1115";
  const isDark = luminance(hexToRgb(bg)) < 0.5;

  return {
    surface1: isDark ? "rgba(255,255,255,.035)" : "rgba(0,0,0,.035)",
    surface2: isDark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.06)",
    surface3: isDark ? "rgba(255,255,255,.085)" : "rgba(0,0,0,.085)",
    divider: isDark ? "rgba(255,255,255,.10)" : "rgba(0,0,0,.12)",
    softShadow: isDark ? "0 10px 28px rgba(0,0,0,.28)" : "0 10px 28px rgba(0,0,0,.10)",
    textPrimary,
    textSecondary,
    accent,
    bg,
    isDark,
  };
};

const panel = (lvl: 1 | 2 | 3, S: Surfaces): React.CSSProperties => ({
  backgroundColor: lvl === 1 ? S.surface1 : lvl === 2 ? S.surface2 : S.surface3,
  borderColor: S.divider,
  borderWidth: 1,
  boxShadow: S.softShadow,
});

/* =========================================================
   LIGHTBOX (zoom, pan, next/prev, keyboard + swipe)
========================================================= */
type LightboxItem = { url: string; alt?: string; fit?: Fit };

const Lightbox: React.FC<{
  items: LightboxItem[];
  index: number;
  onClose: () => void;
  onIndexChange?: (i: number) => void;
  accent: string;
}> = ({ items, index, onClose, onIndexChange, accent }) => {
  const [i, setI] = useState(index);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const startRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const offRef = useRef({ x: 0, y: 0 });
  const touchStartX = useRef<number | null>(null);

  const clampIndex = (n: number) => (n + items.length) % items.length;

  const go = (dir: 1 | -1) => {
    const ni = clampIndex(i + dir);
    setI(ni);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    onIndexChange?.(ni);
  };

  const onWheel: React.WheelEventHandler = (e) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.1 : -0.1;
    setZoom((z) => Math.max(1, Math.min(4, +(z + delta).toFixed(2))));
  };

  const onMouseDown: React.MouseEventHandler = (e) => {
    if (zoom === 1) return;
    setDragging(true);
    startRef.current = { x: e.clientX, y: e.clientY };
    offRef.current = { ...offset };
  };

  const onMouseMove: React.MouseEventHandler = (e) => {
    if (!dragging) return;
    const dx = e.clientX - startRef.current.x;
    const dy = e.clientY - startRef.current.y;
    setOffset({ x: offRef.current.x + dx, y: offRef.current.y + dy });
  };

  const onMouseUp = () => setDragging(false);

  const onTouchStart: React.TouchEventHandler = (e) => {
    if (e.touches.length === 1) touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd: React.TouchEventHandler = (e) => {
    if (touchStartX.current == null) return;
    const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
    if (Math.abs(dx) > 50) go(dx < 0 ? +1 : -1);
    touchStartX.current = null;
  };

  const onDblClick: React.MouseEventHandler = () => {
    setZoom((z) => (z === 1 ? 2 : 1));
    if (zoom !== 1) setOffset({ x: 0, y: 0 });
  };

  const keyHandler = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(+1);
      if (e.key === "ArrowLeft") go(-1);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i, items.length]
  );

  useEffect(() => {
    setI(index);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  }, [index]);

  useEffect(() => {
    document.addEventListener("keydown", keyHandler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", keyHandler);
      document.body.style.overflow = "";
    };
  }, [keyHandler]);

  if (!items.length) return null;
  const curr = items[i];

  return (
    <div className="fixed inset-0 z-[1000] bg-black/90 flex flex-col" aria-modal="true" role="dialog">
      {/* Top bar */}
      <div className="flex items-center justify-between px-3 py-2">
        <div className="text-xs text-white/70">
          {i + 1} / {items.length}
        </div>

        <div className="flex items-center gap-2">
          <button
            className="p-2 rounded-lg bg-white/10 hover:bg-white/20"
            onClick={() => setZoom((z) => Math.max(1, +(z - 0.2).toFixed(2)))}
            title="Uzaklaştır"
          >
            <ZoomOut className="w-5 h-5 text-white" />
          </button>
          <button
            className="p-2 rounded-lg bg-white/10 hover:bg-white/20"
            onClick={() => setZoom((z) => Math.min(4, +(z + 0.2).toFixed(2)))}
            title="Yakınlaştır"
          >
            <ZoomIn className="w-5 h-5 text-white" />
          </button>
          <button
            className="p-2 rounded-lg bg-white/10 hover:bg-white/20"
            onClick={() => {
              setZoom(1);
              setOffset({ x: 0, y: 0 });
            }}
            title="%100"
          >
            <Minimize className="w-5 h-5 text-white" />
          </button>
          <button className="p-2 rounded-lg bg-white/10 hover:bg-white/20" onClick={onClose} title="Kapat">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      {/* Image area */}
      <div
        className={`relative flex-1 flex items-center justify-center overflow-hidden touch-pan-y ${
          zoom > 1 ? (dragging ? "cursor-grabbing" : "cursor-grab") : "cursor-default"
        }`}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onDoubleClick={onDblClick}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {/* prev/next */}
        {items.length > 1 && (
          <>
            <button
              className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 hover:bg-white/20"
              onClick={() => go(-1)}
              aria-label="Önceki"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <button
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 hover:bg-white/20"
              onClick={() => go(+1)}
              aria-label="Sonraki"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          </>
        )}

        <img
          src={curr.url}
          alt={curr.alt || ""}
          className="select-none"
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
            transition: dragging ? "none" : "transform 120ms ease",
            maxWidth: zoom === 1 ? "92vw" : "none",
            maxHeight: zoom === 1 ? "82vh" : "none",
            objectFit: zoom === 1 ? (curr.fit === "cover" ? "cover" : "contain") : "unset",
          }}
          draggable={false}
        />
      </div>

      {/* Dots */}
      {items.length > 1 && (
        <div className="flex items-center justify-center gap-1 py-2">
          {items.map((_, di) => (
            <button
              key={di}
              className="w-2 h-2 rounded-full"
              style={{
                background: di === i ? accent : "rgba(255,255,255,0.35)",
                opacity: di === i ? 1 : 0.8,
              }}
              onClick={() => {
                setI(di);
                setZoom(1);
                setOffset({ x: 0, y: 0 });
                onIndexChange?.(di);
              }}
              aria-label={`Görsel ${di + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* =========================================================
   ANA SAYFA BİLEŞENİ
========================================================= */
const ProjeDetayPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const pid = Number(id);

  const { theme } = useTheme();
  const S = useSurfaces(theme);

  const [data, setData] = useState<ProjeDetailDto | null>(null);
  const [loading, setLoading] = useState(true);

  const galeri = useMemo(() => {
    const arr = parseArr<GalleryImageJson>(data?.fotoGaleriJson)
      .filter((x) => x?.url)
      .sort((a, b) => (a.siraNo ?? 0) - (b.siraNo ?? 0));
    return arr;
  }, [data]);

  const steps = useMemo(() => {
    const arr = parseArr<StepJson>(data?.yapimAsamalariJson);
    arr.forEach((s) => s.fotolar?.sort((a, b) => (a.siraNo ?? 0) - (b.siraNo ?? 0)));
    return arr;
  }, [data]);

  const tags = useMemo(() => parseArr<string>(data?.etiketlerJson).filter(Boolean), [data]);

  const [lbOpen, setLbOpen] = useState(false);
  const [lbItems, setLbItems] = useState<LightboxItem[]>([]);
  const [lbIndex, setLbIndex] = useState(0);

  const openLightbox = (items: LightboxItem[], index: number) => {
    setLbItems(items);
    setLbIndex(index);
    setLbOpen(true);
  };

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const res = await postRequest<{ id: number }, ApiResponse<ProjeDetailDto>>("/Projeler/detay", { id: pid });
      if (res.success && res.data) setData(res.data);
      else setData(null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isNaN(pid)) fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid]);

  /* ------- Ana galeri slider state (kapak + fotoGaleri) ------- */
  const sliderItems = useMemo<LightboxItem[]>(() => {
    const list: LightboxItem[] = [];
    if (data?.kapakFotoUrl) list.push({ url: data.kapakFotoUrl, fit: "cover" });
    for (const g of galeri) list.push({ url: g.url, alt: g.alt, fit: g.fit ?? "contain" });
    return list;
  }, [data, galeri]);

  const [slideIdx, setSlideIdx] = useState(0);
  const swipeStartX = useRef<number | null>(null);

  useEffect(() => {
    setSlideIdx(0);
  }, [data?.id]);

  const goSlide = (dir: -1 | 1) => {
    if (!sliderItems.length) return;
    const next = (slideIdx + dir + sliderItems.length) % sliderItems.length;
    setSlideIdx(next);
  };

  const onGalleryTouchStart: React.TouchEventHandler = (e) => {
    if (e.touches.length === 1) swipeStartX.current = e.touches[0].clientX;
  };
  const onGalleryTouchEnd: React.TouchEventHandler = (e) => {
    if (swipeStartX.current == null) return;
    const dx = (e.changedTouches[0]?.clientX ?? 0) - swipeStartX.current;
    if (Math.abs(dx) > 50) goSlide(dx < 0 ? +1 : -1);
    swipeStartX.current = null;
  };

  if (loading) {
    return (
      <div style={{ backgroundColor: S.bg }}>
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="h-64 rounded-2xl animate-pulse" style={{ backgroundColor: S.surface2 }} />
          <div className="mt-6 space-y-3">
            <div className="h-8 rounded w-1/2 animate-pulse" style={{ backgroundColor: S.surface2 }} />
            <div className="h-4 rounded w-2/3 animate-pulse" style={{ backgroundColor: S.surface2 }} />
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ backgroundColor: S.bg }}>
        <div className="max-w-6xl mx-auto px-4 py-12">
          <p className="text-sm" style={{ color: S.textSecondary }}>
            Proje bulunamadı.
          </p>
          <Link to="/projeler" className="inline-flex items-center gap-2 mt-4 text-sm" style={{ color: S.accent }}>
            <ChevronLeft className="w-4 h-4" /> Projelere dön
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: S.bg }}>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* back */}
        <div className="flex items-center justify-between">
          <Link to="/projeler" className="inline-flex items-center gap-2 text-sm" style={{ color: S.accent }}>
            <ChevronLeft className="w-4 h-4" /> Projelere dön
          </Link>
        </div>

        {/* title + tags */}
        <div className="mt-4">
          <h1 className="text-3xl md:text-4xl font-normal  tracking-tight" style={{ color: S.textPrimary }}>
            {data.baslik}
          </h1>

          {tags.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {tags.map((t, i) => (
                <span
                  key={`${t}_${i}`}
                  className="text-xs px-2.5 py-1 rounded-full border"
                  style={{
                    background: `${S.accent}14`,
                    color: S.accent,
                    borderColor: `${S.accent}33`,
                  }}
                >
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* HERO CAROUSEL (kapak + galeri) */}
        <section className="mt-6 rounded-3xl overflow-hidden border" style={panel(3, S)}>
          <div
            className="relative w-full aspect-[16/10] md:aspect-[16/7]"
            onTouchStart={onGalleryTouchStart}
            onTouchEnd={onGalleryTouchEnd}
            style={{ backgroundColor: S.surface1 }}
          >
            {sliderItems.length ? (
              <>
                <img
                  src={sliderItems[slideIdx].url}
                  alt={sliderItems[slideIdx].alt || ""}
                  className={`absolute inset-0 w-full h-full ${
                    sliderItems[slideIdx].fit === "cover" ? "object-cover" : "object-contain"
                  }`}
                  draggable={false}
                />

                {/* Overlay: alt yazı okunurluğu + premium görünüm */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(to top, rgba(0,0,0,.55) 0%, rgba(0,0,0,.18) 45%, rgba(0,0,0,0) 70%)",
                  }}
                />

                {/* left/right */}
                {sliderItems.length > 1 && (
                  <>
                    <button
                      className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full p-2.5 backdrop-blur"
                      style={{ background: "rgba(0,0,0,.35)", border: `1px solid rgba(255,255,255,.18)` }}
                      onClick={() => goSlide(-1)}
                      aria-label="Önceki"
                    >
                      <ChevronLeft className="w-5 h-5 text-white" />
                    </button>

                    <button
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-2.5 backdrop-blur"
                      style={{ background: "rgba(0,0,0,.35)", border: `1px solid rgba(255,255,255,.18)` }}
                      onClick={() => goSlide(+1)}
                      aria-label="Sonraki"
                    >
                      <ChevronRight className="w-5 h-5 text-white" />
                    </button>

                    {/* dots */}
                    <div className="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-1.5">
                      {sliderItems.map((_, di) => (
                        <button
                          key={di}
                          className="h-2 rounded-full transition-all"
                          style={{
                            width: di === slideIdx ? 18 : 8,
                            background: di === slideIdx ? S.accent : "rgba(255,255,255,.35)",
                          }}
                          onClick={() => setSlideIdx(di)}
                          aria-label={`Slide ${di + 1}`}
                        />
                      ))}
                    </div>
                  </>
                )}

                {/* top-right actions */}
                <div className="absolute top-3 right-3 flex items-center gap-2">
                  <button
                    className="rounded-full p-2.5 backdrop-blur"
                    style={{ background: "rgba(0,0,0,.35)", border: `1px solid rgba(255,255,255,.18)` }}
                    onClick={() => openLightbox(sliderItems, slideIdx)}
                    title="Tam ekran"
                    aria-label="Tam ekran"
                  >
                    <Maximize className="w-5 h-5 text-white" />
                  </button>
                </div>

                {/* bottom-left meta */}
                <div className="absolute bottom-5 left-5">
                  <div className="text-xs text-white/80">
                    {slideIdx + 1} / {sliderItems.length}
                  </div>
                </div>
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center" style={{ color: S.textSecondary }}>
                <ImageIcon className="w-6 h-6 mr-2" /> Görsel yok
              </div>
            )}
          </div>
        </section>

        {/* description */}
        {(data.aciklama || "").trim() !== "" && (
          <section className="mt-6 rounded-2xl border p-5 md:p-6" style={panel(2, S)}>
            <h2 className="text-base font-semibold mb-2" style={{ color: S.textPrimary }}>
              Açıklama
            </h2>
            <div className="text-[15px] leading-7" style={{ color: S.textSecondary }}>
              {data.aciklama}
            </div>
          </section>
        )}

        {/* PROJE FOTO GRID (alt kısım) */}
        {galeri.length > 0 && (
          <section className="mt-10">
            <div className="flex items-end justify-between gap-3 mb-3">
              <h2 className="text-xl font-semibold" style={{ color: S.textPrimary }}>
                Proje Fotoğrafları
              </h2>
              <button
                type="button"
                className="text-sm underline"
                style={{ color: S.textSecondary }}
                onClick={() =>
                  openLightbox(
                    galeri.map((x) => ({ url: x.url, alt: x.alt, fit: x.fit ?? "contain" })),
                    0
                  )
                }
              >
                Tümünü aç
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {galeri.map((g, gi) => (
                <button
                  key={g.url + gi}
                  type="button"
                  className="text-left rounded-2xl overflow-hidden border focus:outline-none transition-transform hover:-translate-y-0.5"
                  style={panel(1, S)}
                  onClick={() =>
                    openLightbox(
                      galeri.map((x) => ({ url: x.url, alt: x.alt, fit: x.fit ?? "contain" })),
                      gi
                    )
                  }
                >
                  <div className="relative aspect-[4/3]" style={{ backgroundColor: S.surface1 }}>
                    <img
                      src={g.url}
                      alt={g.alt || ""}
                      className={`absolute inset-0 w-full h-full ${g.fit === "cover" ? "object-cover" : "object-contain"}`}
                      loading="lazy"
                      draggable={false}
                    />
                    <div
                      className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity pointer-events-none"
                      style={{ background: "linear-gradient(to top, rgba(0,0,0,.35), rgba(0,0,0,0))" }}
                    />
                  </div>

                  {(g.alt || "").trim() !== "" && (
                    <div className="text-xs px-3 py-2" style={{ color: S.textSecondary }}>
                      {g.alt}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </section>
        )}

        {/* STEPS */}
        {steps.length > 0 && (
          <section className="mt-12">
            <h2 className="text-xl font-semibold mb-4" style={{ color: S.textPrimary }}>
              Yapım Aşamaları
            </h2>

            <ol className="space-y-6">
              {steps.map((s, idx) => {
                const stepItems = (s.fotolar ?? []).map((f) => ({ url: f.url, alt: f.alt, fit: f.fit ?? "contain" }));

                return (
                  <li key={`st_${idx}`} className="rounded-2xl overflow-hidden border p-5 md:p-6" style={panel(2, S)}>
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
                      <div>
                        <div className="text-lg font-semibold" style={{ color: S.textPrimary }}>
                          {idx + 1}. {s.adim || "Aşama"}
                        </div>

                        {(s.aciklama || "").trim() !== "" && (
                          <div className="mt-1 text-sm leading-6" style={{ color: S.textSecondary }}>
                            {s.aciklama}
                          </div>
                        )}
                      </div>

                      {s.tarih && (
                        <span
                          className="self-start text-xs px-2.5 py-1 rounded-full border"
                          style={{
                            background: `${S.accent}14`,
                            color: S.accent,
                            borderColor: `${S.accent}33`,
                          }}
                        >
                          {new Date(s.tarih).toLocaleDateString("tr-TR")}
                        </span>
                      )}
                    </div>

                    {s.fotolar && s.fotolar.length > 0 && (
                      <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
                        {s.fotolar.map((f, j) => (
                          <button
                            key={`sf_${idx}_${j}`}
                            type="button"
                            className="rounded-2xl overflow-hidden border text-left transition-transform hover:-translate-y-0.5"
                            style={panel(1, S)}
                            onClick={() => openLightbox(stepItems, j)}
                          >
                            <div className="relative aspect-[4/3]" style={{ backgroundColor: S.surface1 }}>
                              <img
                                src={f.url}
                                alt={f.alt || ""}
                                className={`absolute inset-0 w-full h-full ${
                                  f.fit === "cover" ? "object-cover" : "object-contain"
                                }`}
                                loading="lazy"
                                draggable={false}
                              />
                            </div>
                            {(f.alt || "").trim() !== "" && (
                              <div className="text-xs px-3 py-2" style={{ color: S.textSecondary }}>
                                {f.alt}
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          </section>
        )}

        {/* footer back */}
        <div className="mt-12">
          <Link
            to="/projeler"
            className="inline-flex items-center gap-2 rounded-xl px-4 py-2 border font-semibold"
            style={{ color: S.accent, borderColor: `${S.accent}66`, background: `${S.accent}10` }}
          >
            <ChevronLeft className="w-4 h-4" /> Projelere dön
          </Link>
        </div>

        {/* LIGHTBOX */}
        {lbOpen && (
          <Lightbox
            items={lbItems}
            index={lbIndex}
            onClose={() => setLbOpen(false)}
            onIndexChange={setLbIndex}
            accent={S.accent}
          />
        )}
      </div>
    </div>
  );
};

export default ProjeDetayPage;
